import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import axios from 'axios';

import { useLanguage } from '../context/LanguageContext';

interface BlogPost {
  title: string;
  link: string;
  pubDate: string;
  content: string;
  contentSnippet?: string;
  enclosure?: {
    url: string;
    type: string;
  };
}

export const BlogPosts: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { t } = useLanguage();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get('https://api.rss2json.com/v1/api.json', {
          params: {
            rss_url: 'https://blog.cemates.me/rss/',
            api_key: import.meta.env.VITE_RSS_API_KEY,
            count: 10
          }
        });

        if (response.data.status === 'ok') {
          setPosts(response.data.items.map((item: any) => ({
            title: item.title,
            link: item.link,
            pubDate: item.pubDate,
            content: item.content,
            contentSnippet: item.description.replace(/<[^>]*>/g, ''), // Strip HTML tags
            enclosure: item.thumbnail ? {
              url: item.thumbnail,
              type: 'image/jpeg'
            } : undefined
          })));
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setPosts([{
          title: t('blog', 'errorTitle'),
          link: "https://blog.cemates.me/",
          pubDate: new Date().toISOString(),
          content: t('blog', 'errorMsg'),
          contentSnippet: t('blog', 'errorMsg'),
          enclosure: {
            url: "https://images.pexels.com/photos/11035471/pexels-photo-11035471.jpeg",
            type: "image/jpeg"
          }
        }]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="mb-8">
            {!compact && <div className="h-48 bg-muted rounded-xl mb-4" />}
            <div className="h-6 bg-muted rounded w-3/4 mb-2" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-4">
        {posts.slice(0, 3).map((post, index) => (
          <a
            key={index}
            href={post.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block group"
          >
            <h3 className="text-muted-foreground group-hover:text-primary transition-colors line-clamp-1 flex items-center">
              {post.title}
              <ExternalLink className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h3>
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.slice(0, 6).map((post, index) => (
        <a
          key={index}
          href={post.link}
          target="_blank"
          rel="noopener noreferrer"
          className="group"
        >
          <div className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-border">
            {post.enclosure && (
              <div className="aspect-video w-full overflow-hidden">
                <img
                  src={post.enclosure.url}
                  alt={post.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}
            <div className="p-6">
              <h3 className="text-xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors">
                {post.title}
              </h3>
              <div className="text-muted-foreground line-clamp-2">
                {post.contentSnippet}
              </div>
              <div className="mt-4 flex items-center text-primary">
                {t('blog', 'readMore')} <ExternalLink className="w-4 h-4 ml-1" />
              </div>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
};
